import React from "react";
import MarketingLayout from "./MarketingLayout";
import { useSeo } from "@/lib/seo";

const H2 = ({ children, id }) => (
  <h2
    id={id}
    className="font-display-italic text-3xl md:text-4xl leading-tight tracking-tight mt-12 mb-4 scroll-mt-24"
  >
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-xl leading-tight tracking-tight mt-6 mb-2">
    {children}
  </h3>
);
const P = ({ children }) => (
  <p className="text-[17px] text-[color:var(--ink-2)] leading-snug mb-4 max-w-2xl">
    {children}
  </p>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-6 mb-4 max-w-2xl space-y-1.5 text-[17px] text-[color:var(--ink-2)] leading-snug">
    {children}
  </ul>
);

export default function Privacy() {
  useSeo({
    title: "Privacy – FamFeel Family Calendar",
    description:
      "FamFeel's privacy policy. No ads, no tracking, no email. PINs hashed, data scoped per family, delete-all available from Settings. GDPR / UK-GDPR friendly.",
    path: "/privacy",
    jsonLdId: "ld-privacy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== "undefined" ? window.location.origin + "/" : undefined },
        { "@type": "ListItem", "position": 2, "name": "Privacy", "item": typeof window !== "undefined" ? window.location.origin + "/privacy" : undefined },
      ],
    },
  });
  return (
    <MarketingLayout>
      <section className="px-6 md:px-10 pt-10 md:pt-16 pb-20 max-w-4xl">
        <div className="label-tiny mb-4">Privacy Policy</div>
        <h1
          className="font-display-italic text-6xl md:text-8xl leading-[0.98] tracking-tight mb-6"
          data-testid="privacy-h1"
        >
          Your family,
          <br />
          your data.
        </h1>
        <p className="text-[color:var(--ink-3)] label-tiny mb-10">
          Version 2.1 · Last updated April 2026
        </p>

        {/* Table of contents */}
        <nav
          className="rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-5 mb-10 max-w-2xl"
          aria-label="Contents"
        >
          <div className="label-tiny mb-2">On this page</div>
          <ol className="list-decimal pl-5 space-y-1 text-[15px] text-[color:var(--ink-2)]">
            <li><a href="#short-version" className="underline-offset-2 hover:underline">The short version</a></li>
            <li><a href="#controller" className="underline-offset-2 hover:underline">Who we are</a></li>
            <li><a href="#what-we-store" className="underline-offset-2 hover:underline">What we store</a></li>
            <li><a href="#what-we-dont-store" className="underline-offset-2 hover:underline">What we don't store</a></li>
            <li><a href="#legal-basis" className="underline-offset-2 hover:underline">Legal basis (GDPR / UK-GDPR)</a></li>
            <li><a href="#payments" className="underline-offset-2 hover:underline">Payments</a></li>
            <li><a href="#sub-processors" className="underline-offset-2 hover:underline">Sub-processors</a></li>
            <li><a href="#security" className="underline-offset-2 hover:underline">Security</a></li>
            <li><a href="#retention" className="underline-offset-2 hover:underline">How long we keep things</a></li>
            <li><a href="#children" className="underline-offset-2 hover:underline">Children &amp; under-13 data</a></li>
            <li><a href="#rights" className="underline-offset-2 hover:underline">Your rights</a></li>
            <li><a href="#international" className="underline-offset-2 hover:underline">International transfers</a></li>
            <li><a href="#breach" className="underline-offset-2 hover:underline">If something goes wrong</a></li>
            <li><a href="#changes" className="underline-offset-2 hover:underline">Changes to this policy</a></li>
            <li><a href="#contact" className="underline-offset-2 hover:underline">Contact</a></li>
          </ol>
        </nav>

        <H2 id="short-version">1. The short version</H2>
        <P>
          FamFeel was built as the opposite of a data-hungry app. We don't
          collect emails, we don't show ads, we don't sell anything about you,
          we don't use behavioural-analytics platforms, and nothing you log
          ever leaves your family's private workspace. This page explains
          exactly what we do — and don't — do with your data, in plain English
          and in enough detail to satisfy EU/UK GDPR, the California CCPA, and
          any other jurisdiction that cares (which, to be fair, should be all
          of them).
        </P>

        <H2 id="controller">2. Who we are ("data controller")</H2>
        <P>
          "FamFeel" is operated by the team behind famfeel.com. In the language
          of the GDPR we are the <strong>data controller</strong> for any
          personal data you or your family members enter into the service.
          You can reach us at the address in section 15 (Contact) with any
          privacy question, request, or complaint.
        </P>

        <H2 id="what-we-store">3. What we store</H2>
        <P>
          We store only what's needed to make the app work. Here is the entire
          list — no hidden collections, no "third category" footnotes:
        </P>
        <H3>Family workspace</H3>
        <UL>
          <li><strong>Family name</strong>, 6-letter <strong>join code</strong>, a <em>bcrypt hash</em> of your family PIN (never the PIN itself), timezone, check-in cutoff hour, week-start preference, custom tag/plan presets, and paid plan status.</li>
          <li><strong>Country (optional, self-reported at signup)</strong>: a two-letter country code you pick from a drop-down, or left blank. We never IP-geolocate you — this is the only way FamFeel learns where a family is from, and you can leave it at "Prefer not to say".</li>
          <li><strong>Houses</strong> (Grand and Extended Family tiers): a short list of household labels you chose (e.g. "Mum's", "Dad's"), and which member belongs to each.</li>
        </UL>
        <H3>Members</H3>
        <UL>
          <li>Each member's <strong>name</strong>, avatar emoji, optional <strong>uploaded avatar photo</strong>, role (parent / kid / grandparent / pet / other), birthday (required for people, so the calendar can show a birthday card; pets don't need one), optional PIN (bcrypt-hashed), and — for pets — species.</li>
          <li>Per-member <strong>cross-house privacy toggles</strong> (mood / sleep / status / note / tags) on Grand and Extended Family tiers.</li>
        </UL>
        <P className="text-[color:var(--ink-3)] text-[15px]">
          <em>About uploaded avatar photos</em>: when you upload a photo it is{" "}
          <strong>compressed in your browser</strong> to a 256×256 JPEG (about
          25 KB) <strong>before it leaves your device</strong> — we never
          receive the original full-resolution file. The compressed image is
          stored as a base64 string inside your family's own member record in
          our database, never on a separate image CDN and never linked to any
          URL outside FamFeel. Remove it any time by opening the member in
          Settings and tapping the avatar picker to clear it; the string is
          overwritten on save. Uploaded avatars are a paid-tier feature; the
          Free tier uses emoji-only avatars which store nothing beyond one
          small character.
        </P>
        <H3>Check-ins &amp; activity</H3>
        <UL>
          <li>The <strong>daily check-in</strong> you tap in: sleep, mood, tags, status, optional note, optional <strong>private note</strong> (end-to-end encrypted, see §8), optional today-plan, reactions, and who has "seen" the check-in.</li>
          <li><strong>Pet care</strong>: dog walks (time + who), feed / water-change / mood stamps for other pets, and — for dogs and cats — the small Care log of vet-exam, tick/flea-treatment, bath, and (opt-in for indoor cats) litter-change dates. These live on the pet's member record, are visible to the whole family, and are purely dates you tap in yourself — we never contact a vet, pharmacy, or any outside service.</li>
          <li><strong>Calendar events</strong> you add (title, start/end times, kind, attendees, optional notes, category chips, optional house scope, recurrence rule if any, arrivals/skip stamps, and whether you opt in a push reminder).</li>
          <li><strong>Chores</strong> (title, assignee, repeat cadence, today's completion records).</li>
          <li><strong>Messages</strong> (lightweight in-app notes between members, scoped to your family) &mdash; end-to-end encrypted in your browser, see §8.</li>
        </UL>
        <H3>Operational data (short-lived)</H3>
        <UL>
          <li><strong>Wrong-PIN attempts</strong>: stored for 24 hours so we can rate-limit brute-force attacks, then auto-deleted.</li>
          <li><strong>Push-notification subscriptions</strong>: the anonymous browser/device token (endpoint + keys) each device registers if you turn on push, so we can deliver any reminders you've opted into — including but not limited to; morning summary, walk reminders, birthdays and family nudges. Delete a device from Settings → Notifications and its token is purged.</li>
          <li><strong>Push nudge log</strong>: a single-day record of "member A nudged member B" so the same nudge can't spam someone repeatedly within a day. Rolls off daily.</li>
          <li><strong>Payment transactions</strong>: the Stripe checkout session id, amount, tier, currency, and success timestamp. No card data — ever. See §6.</li>
          <li><strong>Anonymous landing visit counts</strong>: one row per page view with timestamp, path, truncated user-agent, and a salted one-way hash of the source IP. No cookies, no persistent ad profile, no IP geolocation. Bot and crawler user-agents are filtered out at insert time, so this is genuinely a read of real humans visiting the landing page. Used only to know whether the site is working.</li>
        </UL>

        <H2 id="what-we-dont-store">4. What we don't store</H2>
        <UL>
          <li><strong>No email addresses</strong>, phone numbers, or postal addresses.</li>
          <li><strong>No location tracking</strong>. We never ask for device GPS or do IP geolocation. The only "location" in the app is a free-text field you optionally type (e.g. "Grandma's").</li>
          <li><strong>No advertising or behavioural profiles</strong>. No Google Analytics, Facebook Pixel, TikTok Pixel, Hotjar, Amplitude, Mixpanel, Segment, or equivalent.</li>
          <li><strong>No third-party cookies</strong>. We set one first-party token cookie/localStorage item that keeps your family signed in on that browser. That's it.</li>
          <li><strong>No selling, renting, or sharing of your data</strong>. Not now, not ever. If this ever changed the app would stop being FamFeel; we would email you (if we had your email, which we don't) and give you the option to delete your family before any change took effect.</li>
          <li><strong>No AI training</strong>. Your family's check-ins, notes, photos and events are never fed to a language model, ours or anyone else's.</li>
        </UL>

        <H2 id="legal-basis">5. Legal basis (GDPR / UK-GDPR)</H2>
        <P>
          If you're in the EU/UK, we rely on the following legal bases under
          Article 6 of the GDPR:
        </P>
        <UL>
          <li><strong>Contract</strong> (Art. 6(1)(b)): most of what we store is necessary to provide the service you signed up for — the family workspace, check-ins, houses, chores, calendar, payments.</li>
          <li><strong>Legitimate interest</strong> (Art. 6(1)(f)): 24-hour wrong-PIN rate-limiting, anonymous landing-page counts, and server error logs, all balanced against your interest in a secure, working app.</li>
          <li><strong>Consent</strong> (Art. 6(1)(a)): push notifications (only sent if you enable them per device) and optional uploaded photo avatars. Withdraw consent any time from Settings → Notifications or by removing the avatar.</li>
        </UL>
        <P>
          Under the GDPR, "special-category" health data (Article 9) includes
          things like mood. FamFeel only stores the mood you type in yourself,
          for you and your own family to see, and never shares it with anyone
          outside the workspace or uses it for any other purpose — so
          processing is limited to Article 9(2)(a) (your explicit consent by
          choosing to log it).
        </P>

        <H2 id="payments">6. Payments</H2>
        <P>
          If you upgrade to a paid plan (Plus, Grand, or Extended Family),
          payment is handled entirely by <strong>Stripe, Inc.</strong> (San
          Francisco, CA) under their own privacy policy at{" "}
          <a href="https://stripe.com/privacy" className="underline" target="_blank" rel="noopener noreferrer">
            stripe.com/privacy
          </a>
          . We never see or store your card number, expiry, CVC, or billing
          address. Our server only receives — and only retains — the Stripe
          checkout session ID, amount, currency, tier, and paid-until date, so
          we know which plan your family is on and when it expires. Stripe
          webhooks are verified by a signing secret before we accept any
          subscription update. Cancel any time from Settings → Family → Manage
          billing, or from your Stripe-hosted customer portal.
        </P>

        <H2 id="sub-processors">7. Sub-processors</H2>
        <P>
          We use only the following third-party services to operate FamFeel.
          Each processes the minimum data needed to do its job:
        </P>
        <UL>
          <li><strong>Stripe</strong> (payments) — card details, billing country, subscription lifecycle.</li>
          <li><strong>MongoDB Atlas</strong> (database) — all stored family data listed in §3.</li>
          <li><strong>Apple / Google / Microsoft / Mozilla push services</strong> — if you opt in to push notifications, the notification payload transits their push servers (Web Push standard); the payload itself is encrypted with VAPID so they can't read it.</li>
          <li><strong>Our hosting provider</strong> — runs the FamFeel server and serves the web app. Standard GDPR Data Processing Addendum in place.</li>
        </UL>
        <P>
          We do not use Google Analytics, Facebook Pixel, advertising networks,
          session replay tools, AI/ML training APIs, or any other third-party
          telemetry in FamFeel.
        </P>

        <H2 id="security">8. Security</H2>
        <UL>
          <li><strong>Encryption in transit</strong>: HTTPS / TLS on every connection. HSTS header set so browsers refuse to downgrade to HTTP.</li>
          <li><strong>Encryption at rest</strong>: the database provider encrypts storage volumes by default.</li>
          <li><strong>End-to-end encrypted private notes &amp; messages</strong>: when you tick "Private · encrypted" on a check-in note, and for every message posted to the family board, the text is encrypted in your browser (AES-GCM-256, key derived from your family PIN) before it leaves your device. The server only sees ciphertext and cannot decrypt it. Caveat: this protects against a server breach, not someone picking up your unlocked phone. If a device is lost or no longer trusted, open <strong>Settings → Family → Signed-in devices</strong> on any other device to kick it.</li>
          <li><strong>PINs are hashed with bcrypt</strong> — never stored or logged in plaintext. We can't tell you your own PIN; if you forget it a parent has to reset the family.</li>
          <li><strong>Family-scoped access</strong>: every API call requires a JWT bound to one specific family_id. The server explicitly filters every query by family_id — cross-family data access is not possible by design.</li>
          <li><strong>Rate limiting</strong>: per-IP request limits and per-family wrong-PIN lockout after repeated failures.</li>
          <li><strong>Stripe webhook signatures verified</strong> on every inbound billing event; unsigned events are rejected with HTTP 400.</li>
          <li><strong>CORS restricted</strong> to our own origins; no third-party site can call our API on your behalf.</li>
          <li><strong>No source maps in production</strong>; security headers (CSP, X-Content-Type-Options, Referrer-Policy) set conservatively.</li>
        </UL>

        <H2 id="retention">9. How long we keep things</H2>
        <UL>
          <li><strong>Your family workspace</strong> — kept until you delete it (Settings → Family → Danger zone → Delete everything). That action is immediate and irreversible.</li>
          <li><strong>Check-ins, events, chores, messages</strong> — kept for as long as the family workspace exists, which lets any family member open a member's History page and scroll back to re-read past days (Free tier sees the last 14 days; Plus and up see the full range). No automatic deletion. You can delete any individual record from the UI.</li>
          <li><strong>Wrong-PIN attempts</strong> — 24 hours.</li>
          <li><strong>Push nudge log</strong> — same-day only (automatic roll-off).</li>
          <li><strong>Payment transactions</strong> — kept for up to 7 years where legally required for tax/accounting; otherwise purged with the family workspace.</li>
          <li><strong>Anonymous landing visit rows</strong> — retained in aggregate for up to 90 days.</li>
          <li><strong>Backups</strong> — daily database snapshots held for up to 14 days, then overwritten. Deleted families may persist in a backup for that window; after 14 days they are fully gone.</li>
        </UL>

        <H2 id="children">10. Children &amp; under-13 data</H2>
        <P>
          FamFeel is a household tool set up and operated by an adult — a
          parent, guardian, or other responsible caretaker. A child never
          creates the account, never sets the PIN, and never enters
          personal information themselves; a parent does that on their
          behalf. The app does not ask a child for their email, phone
          number, address, school, device ID, or any direct contact
          information — none of those fields exist.
        </P>
        <P>
          For a child under 13 the only personally-identifying data
          FamFeel ever stores is what the parent chose to type in:
        </P>
        <UL>
          <li><strong>The country the family picked</strong> at signup (a two-letter code, optional, family-level — not per-child).</li>
          <li><strong>The child's birthday</strong> (required when the parent adds the child, so the calendar can show a birthday card).</li>
          <li><strong>A first name or nickname</strong> the parent chose, and the emoji or compressed 256×256 photo the parent picked as the avatar.</li>
          <li><strong>The check-ins, moods, sleep, tags and notes</strong> the child (or the parent on their behalf) chose to tap into the app. Nothing else, ever.</li>
        </UL>
        <P>
          We do not track a child's location, we do not collect their
          device fingerprint, we do not profile them, we do not share
          their data with anyone outside the family workspace, and we
          never feed it to an advertising network or an AI training
          model. Section 4 ("What we don't store") applies to children
          exactly as strictly as it applies to adults.
        </P>
        <P>
          <strong>Parental responsibility.</strong> The parent or
          caretaker who set up the family is responsible for (a)
          deciding whether a child is mature enough to tap in their own
          check-ins, (b) the content they choose to type in about a
          child, (c) who they grant access to the family PIN, and (d)
          deleting the data when they no longer want it stored. FamFeel
          provides the tools to do this; the adult makes the calls.
          This is particularly important in any jurisdiction (EU / UK
          under GDPR Article 8, US under COPPA, etc.) where processing
          a child's data requires documented parental consent — by
          creating a family that includes a child, you (the parent)
          confirm that consent.
        </P>
        <P>
          <strong>Deleting a child's data.</strong> At any time, any
          parent in the family can:
        </P>
        <UL>
          <li>Delete an individual child member from <em>Settings → Family</em>. Their check-ins, chores, and any events referencing them are removed with them.</li>
          <li>Delete an individual check-in, event, chore, message, or uploaded avatar from within the UI.</li>
          <li>Delete the entire family workspace from <em>Settings → Family → Danger zone → Delete everything</em>. Immediate and irreversible — every child's data goes with it.</li>
          <li>Request a full JSON export of all their child's data before deletion by emailing us (see §15) — provided at no cost within 30 days.</li>
        </UL>
        <P>
          If a child under 13 has been added to a family without your
          knowledge or consent, email us at the address in §15 with
          enough detail to identify the record and we will remove it
          promptly — typically within one business day.
        </P>

        <H2 id="rights">11. Your rights</H2>
        <P>
          Under the GDPR, UK-GDPR and CCPA, you have the following rights.
          Most are self-service in the app:
        </P>
        <UL>
          <li><strong>Access</strong>: every piece of stored data is visible to parents in the app — check-ins, events, members, houses, chores, payment history.</li>
          <li><strong>Rectification</strong>: edit any member, check-in, event, or setting directly in the UI.</li>
          <li><strong>Erasure</strong> ("right to be forgotten"): remove individual members (their check-ins and chores go with them), or delete the entire family workspace from Settings → Family → Danger zone. Immediate, irreversible, cascades to every related record.</li>
          <li><strong>Portability</strong>: on request (contact us in §15) we'll export your family's data as JSON within 30 days, at no cost.</li>
          <li><strong>Restriction / Objection</strong>: pause push notifications per-device at any time; contact us to restrict other processing.</li>
          <li><strong>Withdraw consent</strong>: turn off push in Settings → Notifications; remove an uploaded avatar photo in the member edit screen.</li>
        </UL>
        <P>
          You also have the right to lodge a complaint with your local data-protection authority.
        </P>

        <H2 id="international">12. International transfers</H2>
        <P>
          The database and application servers run in EU / US data centres.
          Where personal data is transferred from the EU/UK to a non-adequacy
          country (currently Stripe US), we rely on Standard Contractual
          Clauses (SCCs) in place with the sub-processor. No data is ever
          transferred to jurisdictions subject to government surveillance
          programmes disclosed at the time of writing (e.g. China, Russia).
        </P>

        <H2 id="breach">13. If something goes wrong</H2>
        <P>
          Despite every reasonable measure, no system is uncrackable. If we
          become aware of a data breach that affects you, we will:
        </P>
        <UL>
          <li>Notify the relevant data-protection authority within <strong>72 hours</strong>, as required by GDPR Article 33.</li>
          <li>Publish a plain-English incident note on famfeel.com describing what happened, what data was involved, what we did to contain it, and what you should do in response.</li>
          <li>Trigger an in-app banner at the top of the dashboard so every device that opens FamFeel sees the notice on next login.</li>
        </UL>

        <H2 id="changes">14. Changes to this policy</H2>
        <P>
          We'll update this page when we add new features that affect data, or
          when laws change. Material changes trigger a banner at the top of
          the app for 30 days and a version bump visible at the top of this
          page. We don't "silently" change the rules — if something meaningful
          shifts you will see it the next time you open FamFeel.
        </P>

        <H2 id="contact">15. Contact</H2>
        <P>
          Questions, requests, or just curious? Email{" "}
          <a href="mailto:famfeel.com@gmail.com" className="underline">
            famfeel.com@gmail.com
          </a>
          . We aim to respond within 7 calendar days.
        </P>
        <P className="text-[color:var(--ink-3)] text-sm">
          This policy is written to satisfy the EU GDPR, UK-GDPR, California
          CCPA and general good-faith privacy practice. Nothing in it is
          intended to create a lesser standard than the law requires — if a
          law in your jurisdiction grants you stronger rights, those stronger
          rights apply.
        </P>
      </section>
    </MarketingLayout>
  );
}
